import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { loginRequest } from '@oms/contracts';
import { useLogin, useSession } from '../../api/auth';
import { isApiError } from '../../api/client';
import { Button, FormErrors, PageLoading, TextField } from '../../components';
import { errorsFromApi, noErrors, validate, type FormErrorState } from '../../lib/forms';
import { safeNext } from '../../lib/navigation-helpers';
import { AuthCard } from '../../layout/PublicLayout';
import { BrandHeader } from './BrandHeader';
import { useBrandingForPage } from './useBrandingForPage';

const FIELDS = { email: { label: 'Email', id: 'login-email' }, password: { label: 'Password', id: 'login-password' } };

export function LoginPage({ scope }: { scope: 'tenant' | 'platform' }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const branding = useBrandingForPage();
  const session = useSession();
  const login = useLogin(scope);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrorState>(noErrors);
  const platform = scope === 'platform';
  const home = platform ? '/admin' : '/';

  useEffect(() => {
    document.title = platform ? 'Platform sign in · Zeriti' : 'Sign in · Zeriti';
  }, [platform]);

  if (!platform && branding.data?.platformAdminHost) return <Navigate to={`/admin/login${window.location.search}`} replace />;
  if (session.isPending || branding.isPending) return <PageLoading />;
  // Already signed in with the right kind of session: continue.
  if (session.data && session.data.scope === scope && !login.isPending) {
    return <Navigate to={safeNext(params.get('next'), home)} replace />;
  }

  const tenant = platform ? null : (branding.data?.tenant ?? null);
  const tenantSlug = tenant?.slug ?? branding.slugParam ?? undefined;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const v = validate(loginRequest, { email, password, tenantSlug });
    if (!v.ok) return setErrors(v.errors);
    setErrors(noErrors);
    login.mutate(v.data, {
      onSuccess: (s) => {
        setPassword('');
        const next = safeNext(params.get('next'), home);
        if (s.scope === 'tenant' && !s.tenant) navigate(`/select-workspace?next=${encodeURIComponent(next)}`, { replace: true });
        else navigate(next, { replace: true });
      },
      onError: (err) => {
        setPassword('');
        if (isApiError(err) && err.status === 401) setErrors({ fields: {}, general: err.message || 'The email or password is incorrect.', requestId: err.requestId });
        else setErrors(errorsFromApi(err));
      },
    });
  };

  return (
    <AuthCard brand={<BrandHeader tenant={tenant} platform={platform} subtitle={platform ? 'Platform administrators only' : tenant ? undefined : 'Order management'} />}>
      <h1 style={{ fontSize: 20 }}>{platform ? 'Platform sign in' : 'Sign in'}</h1>
      {!platform && branding.slugParam && !tenant && (
        <div className="notice notice-warning">No active workspace matches “{branding.slugParam}”. You can still sign in with your account.</div>
      )}
      <FormErrors errors={errors} fields={FIELDS} />
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField id={FIELDS.email.id} label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.fields.email} required autoFocus />
        <TextField
          id={FIELDS.password.id}
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.fields.password}
          required
        />
        <Button type="submit" variant="primary" loading={login.isPending}>
          Sign in
        </Button>
      </form>
      <div className="auth-footer">
        <Link to={`/forgot-password${platform ? '?admin=1' : ''}`}>Forgot your password?</Link>
      </div>
    </AuthCard>
  );
}
